// src/utils/cleanForSpeech.js
// Strips markdown + symbols so TTS reads cleanly

export function cleanForSpeech(text) {
  if (!text) return "";

  let cleaned = text

    // ── Code blocks (```...```) — skip entirely ──────────────────
    .replace(/```(?:[\w-]+)?\n?([\s\S]*?)```/g, (_match, codeContent = "") => {
      const speakableBlock = String(codeContent)
        .replace(/\r/g, "")
        .split("\n")
        .map((line) => line.trim())
        .filter(Boolean)
        .join(". ");

      return speakableBlock ? `${speakableBlock}. ` : "";
    })

    // ── Inline code ───────────────────────────────────────────────
    .replace(/`([^`]*)`/g, "$1")

    // ── Headings (#, ##, ###) ─────────────────────────────────────
    .replace(/^#{1,6}\s+/gm, "")

    // ── Bold / Italic (**text**, *text*, __text__, _text_) ────────
    .replace(/\*\*\*(.+?)\*\*\*/g, "$1")
    .replace(/\*\*(.+?)\*\*/g, "$1")
    .replace(/\*(.+?)\*/g, "$1")
    .replace(/___(.+?)___/g, "$1")
    .replace(/__(.+?)__/g, "$1")
    .replace(/_(.+?)_/g, "$1")

    // ── Blockquote markers (『 』 and >) ──────────────────────────
    .replace(/『/g, " ")
    .replace(/』/g, " ")
    .replace(/^>\s*/gm, "")

    // ── Horizontal rules (--- or ***) ────────────────────────────
    .replace(/^[-*]{3,}\s*$/gm, "")

    // ── Bullet/list markers (-, *, ~, ▸, •) ──────────────────────
    .replace(/^[\s]*[-*~▸•]\s+/gm, "")

    // ── Numbered lists (1. 2. etc) ────────────────────────────────
    .replace(/^\d+\.\s+/gm, "")

    // ── Special symbols used in DharmaAnswers cards ───────────────
    .replace(/✧/g, "")
    .replace(/✨/g, "")
    .replace(/🌿/g, "")
    .replace(/🪔/g, "")
    .replace(/🌺/g, "")
    .replace(/🔍/g, "")
    .replace(/🛠️/g, "")
    .replace(/📚/g, "")
    .replace(/📜/g, "")
    .replace(/💫/g, "")
    .replace(/🌈/g, "")
    .replace(/🕉️/g, "")
    .replace(/🙏/g, "")

    // ── Remove ALL remaining emoji (Unicode ranges) ───────────────
    .replace(
      /[\u{1F300}-\u{1FFFF}\u{2600}-\u{27BF}\u{FE00}-\u{FE0F}]/gu,
      ""
    )

    // ── Links [text](url) → just text ────────────────────────────
    .replace(/\[([^\]]+)\]\([^)]+\)/g, "$1")

    // ── Images ![alt](url) → remove ──────────────────────────────
    .replace(/!\[[^\]]*\]\([^)]+\)/g, "")

    // ── HTML tags ─────────────────────────────────────────────────
    .replace(/<[^>]+>/g, "")

    // ── Dashes used as decorators (— —) ─────────────────────────
    .replace(/---/g, ". ")
    .replace(/--/g, ", ")

    // ── Multiple blank lines → single newline ─────────────────────
    .replace(/\n{3,}/g, "\n\n")

    // ── Trim leading/trailing whitespace ─────────────────────────
    .trim();

  return cleaned;
}

// Split text into sentence chunks for Chrome's TTS bug fix.
// Chrome stops speaking after ~200 chars — we feed it one sentence at a time.
export function splitIntoChunks(text) {
  if (!text) return [];

  // Split on sentence-ending punctuation, keeping the delimiter
  const sentences = text
    .split(/(?<=[।.!?])\s+/)   // Hindi danda + English punctuation
    .map((s) => s.trim())
    .filter((s) => s.length > 0);

  // If a "sentence" is still very long (no punctuation), split at commas
  const chunks = [];
  for (const sentence of sentences) {
    if (sentence.length <= 220) {
      chunks.push(sentence);
    } else {
      // Split long chunk at commas
      const parts = sentence.split(/,\s+/);
      let current = "";
      for (const part of parts) {
        if ((current + part).length <= 220) {
          current += (current ? ", " : "") + part;
        } else {
          if (current) chunks.push(current);
          current = part;
        }
      }
      if (current) chunks.push(current);
    }
  }

  return chunks.filter((c) => c.trim().length > 0);
}
